import type { ImpactBriefPayload } from '../services/impact-brief.service';

/**
 * Which panel a no-pair dashboard state shows — IB4 / DASHBOARD_HOME ladder 4.
 *
 * Before the Impact Brief, every one of these states rendered the same dead end:
 * "capture a second crawl and come back". That is only true when there is
 * nothing to work on. A domain with an analysed inventory has a full Issues
 * queue whether or not it has ever been crawled twice, and the week plan is
 * composed from gap and opportunity, which need no pair.
 *
 * So the choice is not "movement or nothing" but which of four honest answers
 * applies, and they are deliberately four rather than one: sending an operator
 * with sixty open Issues rows to Setup is the specific failure this replaces.
 */
export type ImpactBriefFirstRunPanel =
  /** The brief has not answered yet. Hold the space; do not flash a dead end. */
  | 'loading'
  /** Packages composed from gap / opportunity. The point of ladder 4. */
  | 'packages'
  /** A domain resolved, but nothing has ever been analysed on it. Setup. */
  | 'no-analysis'
  /** The queue has rows; none grouped into a package. Issues, never Setup. */
  | 'no-packages'
  /**
   * No payload to read. Two situations produce it and both want the same
   * answer, which is why they share a panel: the brief could not be read
   * (network failure, or the production `ACTION_PLAN_V2_ENABLED` gate the
   * endpoint sits behind), or the workspace genuinely has nothing at all — the
   * route returns `data: null` when it cannot resolve a domain from captured
   * runs *or* tracked URLs. Either way the surface falls back to its
   * pre-packages copy, which already says "capture a crawl", rather than
   * showing an error for a section that is not the point of the screen.
   */
  | 'unavailable';

export function impactBriefFirstRunPanel(
  brief: ImpactBriefPayload | null,
  briefLoading: boolean
): ImpactBriefFirstRunPanel {
  if (briefLoading) return 'loading';
  if (!brief) return 'unavailable';
  if (brief.packages.length > 0) return 'packages';
  if (brief.emptyState === 'no_analysis') return 'no-analysis';

  // Everything else falls to 'no-packages', including a payload that reports no
  // packages without saying why. The two ways to be wrong here are not equal:
  // offering Issues to someone with an empty queue costs them one click, while
  // offering Setup to someone with a full one tells them to re-run work they
  // have already done.
  return 'no-packages';
}
